import {combineEpics} from 'redux-observable';
import {from, of, EMPTY} from 'rxjs';
import {
    mergeMap,
    catchError,
    filter,
    withLatestFrom,
    map,
    switchMap,
    takeUntil,
} from 'rxjs/operators';
import type {GatewayError} from '@yandex-data-ui/gateway';

import {PageDetailsField, DocTypeContent, PageErrors, UserPermissions} from '~shared/models';

import {includes, isEnum} from '~ui/utils';
import toaster from '~ui/utils/toaster';
import {i18n} from '~ui/components/PageDoc/i18n';
import {i18n as pageI18n} from '~ui/components/WikiPage/i18n';
import {batchActions} from '~ui/store/actions';
import {getPageDetails, updatePageDetails} from '~ui/services/pages';

import {actions as backgroundActions} from '../backgrounds';
import {actions as navigationTreeActions} from '../navigation-tree';
import {actions as userActions} from '../users/slice';
import {WikiEpic} from '../../types';

import {SetPageDetailsData, getPageRedirectUrl} from './utils';
import {actions} from './slice';

const getMissingDetailsFields = [
    PageDetailsField.Authors,
    PageDetailsField.Bookmark,
    PageDetailsField.Cluster,
    PageDetailsField.Subscription,
    PageDetailsField.Permissions,
    PageDetailsField.Background,
    PageDetailsField.Redirect,
    PageDetailsField.LastRevisionId,
    PageDetailsField.Actuality,
    PageDetailsField.Attributes,
] as const;

export const getDetailsFields = [
    PageDetailsField.Breadcrumbs,
    PageDetailsField.Content,
    PageDetailsField.Access,
    PageDetailsField.LastRevisionId,

    ...getMissingDetailsFields,
] as const;

const getRevisionDetailsFields = [PageDetailsField.Content, PageDetailsField.Background] as const;

const getPageDetailsEpic: WikiEpic = (action$, _state$) =>
    action$.pipe(
        filter(actions.getDetails.match),
        switchMap(({payload: {slug: pagePath, noredirect, revisionId, isRoot}}) => {
            return from(
                getPageDetails({
                    slug: pagePath,
                    revisionId,
                    fields: getDetailsFields,
                    raiseOnRedirect: !noredirect,
                }),
            ).pipe(
                map((response) => {
                    const {
                        slug,
                        page_type: pageType,
                        authors,
                        bookmark,
                        cluster,
                        subscription,
                        user_permissions: userPermissions,
                        last_revision_id: lastRevisionId,
                        active_revision: revision,
                        attributes,
                        content,
                        background,
                        redirect,
                        access,
                        actuality,
                        ...rest
                    } = response;

                    const pageDetails: SetPageDetailsData = {
                        slug,
                        pageType,
                        bookmarkId: bookmark?.id,
                        clusterSlug: cluster?.slug,
                        subscription: subscription?.status,
                        userPermissions,
                        backgroundId: background?.id,
                        lastRevisionId,
                        revision: revision || undefined,
                        access: access
                            ? {
                                  type: access.type,
                                  isReadonly: access.is_readonly,
                                  hasExternal: access.has_external,
                                  inheritsFrom: access.inherits_from,
                              }
                            : undefined,
                        actuality: actuality
                            ? {
                                  status: actuality.status,
                                  markedAt: actuality.marked_at,
                                  user: actuality.user,
                                  actualPages: actuality.actual_pages,
                                  externalLinks: actuality.external_links,
                                  comment: actuality.comment,
                              }
                            : undefined,
                        ...rest,
                    };

                    if (isRoot) {
                        pageDetails.toolboxType = 'root';
                    }

                    if (redirect) {
                        const {page_type: redirectPageType, ...restRedirect} =
                            redirect.redirect_target;

                        pageDetails.redirect = {
                            pageType: redirectPageType,
                            ...restRedirect,
                        };
                    }

                    return batchActions([
                        actions.setCurrentDetails(pageDetails),
                        actions.setAuthors({slug, authors}),

                        actions.setAttributes({slug, attributes}),
                        actions.setContent({
                            slug,
                            pageContent: {type: pageType, content} as DocTypeContent,
                        }),
                        actions.setPath({path: pagePath, slug}),
                        userActions.setMany(authors ? authors.all.concat(authors.owner) : []),
                        navigationTreeActions.setHasNavigation(Boolean(cluster)),
                        background && backgroundActions.setBackground(background),
                    ]);
                }),
                catchError((error: GatewayError) => {
                    console.error(error);

                    let errorCode = PageErrors.General;

                    if (isEnum(error.code, PageErrors)) {
                        errorCode = error.code;
                    }

                    if (errorCode === PageErrors.RedirectChain) {
                        const errorDetails =
                            error.details &&
                            (error.details.data as {
                                target_page: {
                                    slug: string;
                                };
                            });

                        if (errorDetails) {
                            return of(
                                batchActions([
                                    actions.setLoading({isLoading: false}),
                                    actions.setRedirectTo({
                                        url: getPageRedirectUrl(errorDetails.target_page.slug),
                                    }),
                                ]),
                            );
                        }
                    }

                    return of(
                        actions.getErrorPageDetails({
                            error: errorCode,
                            slug: pagePath,
                        }),
                    );
                }),
            );
        }),
    );

const stubErrors = [PageErrors.NotFound, PageErrors.Forbidden] as const;

const getErrorPageDetailsEpic: WikiEpic = (action$, _state$, {sdk}) =>
    action$.pipe(
        filter(actions.getErrorPageDetails.match),
        mergeMap(({payload: {slug: pagePath, error: errorCode}}) => {
            if (!includes(stubErrors, errorCode)) {
                return of(
                    batchActions([
                        actions.setLoading({isLoading: false}),
                        navigationTreeActions.setHasNavigation(false),
                        actions.setError({error: errorCode}),
                        actions.setToolboxType({type: 'empty'}),
                    ]),
                );
            }

            const promise =
                errorCode === PageErrors.Forbidden
                    ? sdk.wiki.getForbiddenStubPageDetails({slug: pagePath})
                    : sdk.wiki.getStubPageDetails({slug: pagePath});

            return from(promise).pipe(
                map((res) => {
                    const data = {
                        // init uncommon properties with `undefined`
                        // so that TS won't show an error below
                        authors: undefined,
                        pending_access_request: undefined,
                        moved_out_pages: undefined,
                        access_approvers: undefined,
                        acl: undefined,
                        ...res,
                    };
                    const {
                        slug,
                        cluster,
                        breadcrumbs,
                        user_permissions: userPermissions,
                        moved_out_pages: movedOutPages,
                        authors,
                        pending_access_request: pendingAccessRequest,
                        access_approvers: accessApprovers,
                        acl,
                        ...rest
                    } = data;

                    return batchActions([
                        actions.setError({error: errorCode}),
                        actions.setCurrentStubDetails({
                            slug,
                            breadcrumbs,
                            clusterSlug: cluster?.slug,
                            userPermissions,
                            movedOutPages,
                            pendingAccessRequest,
                            accessApprovers,
                            ...rest,
                        }),
                        actions.setAuthors({slug, authors}),
                        actions.setPageAcl({acl, slug}),
                        actions.setPath({path: pagePath, slug}),
                        actions.setToolboxType({type: 'stub'}),
                        navigationTreeActions.setHasNavigation(Boolean(cluster)),
                        userActions.setMany(authors ? authors.all.concat(authors.owner) : []),
                    ]);
                }),
                catchError((error: GatewayError) => {
                    console.error(error);

                    return of(
                        batchActions([
                            actions.setLoading({isLoading: false}),
                            navigationTreeActions.setHasNavigation(false),
                            actions.setError({error: errorCode}),
                            actions.setToolboxType({type: 'empty'}),
                        ]),
                    );
                }),
            );
        }),
    );

const updatePageTitleEpic: WikiEpic = (action$, state$) =>
    action$.pipe(
        filter(actions.updateTitle.match),
        withLatestFrom(state$),
        mergeMap(
            ([
                {
                    payload: {id: pageId, title},
                },
                state,
            ]) => {
                return from(
                    updatePageDetails({
                        pageId,
                        title,
                        fields: [PageDetailsField.Breadcrumbs, PageDetailsField.Attributes],
                    }),
                ).pipe(
                    map(({slug, page_type: pageType, attributes, ...rest}) => {
                        return batchActions([
                            actions.setDetails({slug, pageType, ...rest}),
                            actions.setAttributes({slug, attributes}),
                        ]);
                    }),
                    catchError((error: GatewayError) => {
                        let content = '';

                        switch (error.code) {
                            case 'VALIDATION_ERROR':
                                content = i18n('error-title-validation');
                                break;
                        }

                        toaster.createToast({
                            type: 'error',
                            name: actions.updateTitle.type,
                            title: i18n('error-title'),
                            content,
                        });

                        return of(
                            actions.setTitle({id: pageId, title: state.pages.preservedTitle}),
                        );
                    }),
                );
            },
        ),
    );

const updatePageAuthorsEpic: WikiEpic = (action$, _state$) =>
    action$.pipe(
        filter(actions.updateAuthors.match),
        mergeMap(({payload: {id: pageId, authors, owner, preventSelflock, forceUpdateDetails}}) => {
            return from(
                updatePageDetails({
                    preventSelflock,
                    pageId,
                    authors: {
                        all: authors,
                        owner,
                    },
                    fields: [
                        PageDetailsField.Permissions,
                        PageDetailsField.Authors,
                        PageDetailsField.Attributes,
                    ],
                }),
            ).pipe(
                map(({slug, authors, attributes, user_permissions: userPermissions}) => {
                    // To show error on page
                    if (
                        preventSelflock === 0 ||
                        (forceUpdateDetails && userPermissions.includes(UserPermissions.CanView))
                    ) {
                        return actions.getDetails({
                            slug,
                        });
                    }

                    return batchActions([
                        actions.setPermissions({slug, permissions: userPermissions}),
                        actions.setAuthors({slug, authors}),
                        userActions.setMany(authors.all.concat(authors.owner)),
                        actions.setAttributes({slug, attributes}),
                    ]);
                }),
                catchError((error: GatewayError) => {
                    return of(
                        batchActions([
                            actions.setAuthorsLoadingById({id: pageId, isLoading: false}),
                            actions.setAuthorsError({id: pageId, error: error.code}),
                        ]),
                    );
                }),
            );
        }),
    );

const getPermissionsEpic: WikiEpic = (action$, _state$, {sdk}) =>
    action$.pipe(
        filter(actions.getPermissions.match),
        mergeMap(({payload: {id}}) => {
            return from(
                sdk.wiki.getPageDetails({
                    slug: id,
                    fields: [PageDetailsField.Permissions],
                }),
            ).pipe(
                map((res) => {
                    const {
                        page_type: pageType,
                        user_permissions: userPermissions,
                        subscription,
                        redirect,
                        access,
                        actuality,
                        ...rest
                    } = res;

                    return actions.setDetails({
                        pageType,
                        userPermissions,
                        ...rest,
                    });
                }),
                catchError((error) => {
                    console.error(error);

                    return EMPTY;
                }),
            );
        }),
    );

// Дозагружаем инфу для страниц с сервера
// Удалить когда перейдем на сервере на новую ручку
// TODO https://st.yandex-team.ru/WIKI-16153
const getMissingPageDetailsEpic: WikiEpic = (action$, _state$) =>
    action$.pipe(
        filter(actions.getMissingDetails.match),
        switchMap(({payload: {slug: pagePath, isRoot}}) => {
            return from(getPageDetails({slug: pagePath, fields: getMissingDetailsFields})).pipe(
                map((response) => {
                    const {
                        slug,
                        page_type: pageType,
                        authors,
                        bookmark,
                        cluster,
                        subscription,
                        user_permissions: userPermissions,
                        background,
                        redirect,
                        last_revision_id: lastRevisionId,
                        active_revision: revision,
                        actuality,
                        attributes,
                        ...rest
                    } = response;

                    const pageDetails: SetPageDetailsData = {
                        slug,
                        pageType,
                        bookmarkId: bookmark?.id,
                        clusterSlug: cluster?.slug,
                        subscription: subscription?.status,
                        userPermissions,
                        backgroundId: background?.id,
                        revision: revision || undefined,
                        lastRevisionId,
                        actuality: actuality
                            ? {
                                  status: actuality.status,
                                  markedAt: actuality.marked_at,
                                  user: actuality.user,
                                  actualPages: actuality.actual_pages,
                                  externalLinks: actuality.external_links,
                                  comment: actuality.comment,
                              }
                            : undefined,
                        ...rest,
                    };

                    if (isRoot) {
                        pageDetails.toolboxType = 'root';
                    }

                    if (redirect) {
                        const {page_type: redirectPageType, ...restRedirect} =
                            redirect.redirect_target;

                        pageDetails.redirect = {
                            pageType: redirectPageType,
                            ...restRedirect,
                        };
                    }

                    return batchActions([
                        actions.setCurrentDetails(pageDetails),
                        actions.setPath({path: pagePath, slug}),
                        actions.setAuthors({slug, authors}),
                        actions.setAttributes({slug, attributes}),

                        userActions.setMany(authors ? authors.all.concat(authors.owner) : []),
                        navigationTreeActions.setHasNavigation(Boolean(cluster)),
                        background && backgroundActions.setBackground(background),
                    ]);
                }),
                takeUntil(
                    action$.ofType(
                        actions.getDetails.type,
                        actions.getMissingDetails.type,
                        actions.getRevisionDetails.type,
                    ),
                ),
            );
        }),
    );

const getRevisionDetailsEpic: WikiEpic = (action$, _state$) =>
    action$.pipe(
        filter(actions.getRevisionDetails.match),
        mergeMap(({payload: {pagePath, revisionId}}) => {
            return from(
                getPageDetails({slug: pagePath, fields: getRevisionDetailsFields, revisionId}),
            ).pipe(
                map((response) => {
                    const {
                        slug,
                        page_type: pageType,
                        background,
                        content,
                        active_revision: revision,
                        ...rest
                    } = response;

                    const pageDetails: SetPageDetailsData = {
                        slug,
                        pageType,
                        backgroundId: background?.id,
                        revision: revision || undefined,
                        ...rest,
                    };

                    return batchActions([
                        actions.setCurrentDetails(pageDetails),
                        actions.setPath({path: pagePath, slug}),
                        actions.setContent({
                            slug,
                            pageContent: {type: pageType, content} as DocTypeContent,
                        }),

                        background && backgroundActions.setBackground(background),
                    ]);
                }),
                catchError((_error: GatewayError) => {
                    toaster.createToast({
                        type: 'error',
                        name: actions.getRevisionDetails.type,
                        title: pageI18n('error-get-revision'),
                    });

                    return of(actions.setLoading({isLoading: false}));
                }),
            );
        }),
    );

const epic = combineEpics(
    getPageDetailsEpic,
    getMissingPageDetailsEpic,
    updatePageTitleEpic,
    updatePageAuthorsEpic,
    getErrorPageDetailsEpic,
    getPermissionsEpic,
    getRevisionDetailsEpic,
);

export {epic};
